import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

interface ModalStackContextValue {
  stack: string[];
  pushModal: (id: string) => number; // Returns z-index layer
  popModal: (id: string) => void;
  isTopModal: (id: string) => boolean;
  getZIndexForModal: (id: string) => number;
}

const ModalStackContext = createContext<ModalStackContextValue | null>(null);

let bodyScrollLockCount = 0;

export const ModalStackProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [stack, setStack] = useState<string[]>([]);

  const lockBodyScroll = () => {
    bodyScrollLockCount++;
    if (typeof document !== 'undefined') {
      document.body.style.overflow = 'hidden';
    }
  };

  const unlockBodyScroll = () => {
    bodyScrollLockCount = Math.max(0, bodyScrollLockCount - 1);
    if (bodyScrollLockCount === 0 && typeof document !== 'undefined') {
      document.body.style.overflow = '';
    }
  };

  const pushModal = useCallback((id: string): number => {
    lockBodyScroll();
    setStack((prev) => Array.from(new Set([...prev, id])));
    return 30 + stack.length * 10;
  }, [stack.length]);

  const popModal = useCallback((id: string) => {
    unlockBodyScroll();
    setStack((prev) => prev.filter((mId) => mId !== id));
  }, []);

  const isTopModal = useCallback((id: string): boolean => {
    return stack.length > 0 && stack[stack.length - 1] === id;
  }, [stack]);

  const getZIndexForModal = useCallback((id: string): number => {
    const idx = stack.indexOf(id);
    if (idx === -1) return 30;
    return 30 + idx * 10;
  }, [stack]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (typeof document !== 'undefined') {
        document.body.style.overflow = '';
      }
      bodyScrollLockCount = 0;
    };
  }, []);

  return (
    <ModalStackContext.Provider
      value={{
        stack,
        pushModal,
        popModal,
        isTopModal,
        getZIndexForModal,
      }}
    >
      {children}
    </ModalStackContext.Provider>
  );
};

export const useModalStack = (): ModalStackContextValue => {
  const ctx = useContext(ModalStackContext);
  if (!ctx) {
    return {
      stack: [],
      pushModal: () => 30,
      popModal: () => {},
      isTopModal: () => true,
      getZIndexForModal: () => 30,
    };
  }
  return ctx;
};
